import { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import AWS from 'aws-sdk';
import DropdownFranchise from '../../components/dropdowns/DropdownFranchise';
import DropdownLocationMultiple from '../../components/dropdowns/DropdownLocationMultiple';
import { useData } from '../../hooks/useData';
import VideoPlayerKinesis from '../../components/cameras/VideoPlayerKinesis';
import MetricsDisplayer from '../../components/cameras/MetricsDisplayer';
import LiveButton from '../../components/cameras/LiveButton';
import { getMothershipMetrics } from '../../utils/api';
import { useCamera } from '../../hooks/useCamera';
import { Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Roles } from '@/types/Roles';
import { MONITORSTREAMSENABLED, CVDTENABLED } from '../../constants';
import CvToggleButton from '../../components/cameras/CvToggleButton';

const Mothership = () => {
  const [showMetricsView, setShowMetricsView] = useState(false);
  const [allLocationMetrics, setAllLocationMetrics] = useState<Map<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedSortMetric, setSelectedSortMetric] = useState<string | null>(null);
  const [reverseOrder, setReverseOrder] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState<number | 'all'>(8);
  const [showCvToggle, setShowCvToggle] = useState(true);

  // Initialize showMetrics with new metrics set to false
  const [showMetrics, setShowMetrics] = useState({
    internetConnection: true,
    ramUsage: true,
    diskUsage: true,
    uploadSpeed: true,
    restartCv: true,
    restartAll: true,
    collectionTimestamp: true,
    cameras: true,
    sudoCrontab: false,
    envContents: false,
    freeSwap: false,
    internalIp: false,
  });

  const {
    isGrid,
    setKinesisVideo,
    showCvStream,
    setShowCvStream,
    showCvDtStream,
    setShowCvDtStream,
    showMonitorStream,
    setShowMonitorStream,
    hasCvStream,
    setHasCvStream,
    hasCvDtStream,
    setHasCvDtStream,
    hasMonitorStream,
    setHasMonitorStream,
  } = useCamera();

  const { selectedFranchise, selectedLocations, profile } = useData();

  const isHello = profile?.franchise.id === '29' || profile?.franchise.id === '59';
  const shouldHaveMonitorStreams =
    (profile?.franchise.userRole === Roles.ADMIN && MONITORSTREAMSENABLED.includes(selectedFranchise?.id ?? '')) ||
    (profile?.franchise.userRole === Roles.ADMIN && isHello && selectedFranchise?.id === '29');
  const shouldHaveCVDTStream =
    profile?.franchise.userRole === Roles.ADMIN && CVDTENABLED.includes(selectedFranchise?.id ?? '');

  const sortedLocations = selectedSortMetric === 'collectionTimestamp'
    ? [...selectedLocations].sort((a, b) => {
        const aTimestamp = allLocationMetrics?.get(a.id)?.timestamp
          ? new Date(allLocationMetrics.get(a.id).timestamp).getTime()
          : 0;
        const bTimestamp = allLocationMetrics?.get(b.id)?.timestamp
          ? new Date(allLocationMetrics.get(b.id).timestamp).getTime()
          : 0;
        return aTimestamp - bTimestamp;
      })
    : selectedSortMetric
    ? [...selectedLocations].sort((a, b) => {
        const aMetric = allLocationMetrics?.get(a.id)?.[selectedSortMetric] ?? 0;
        const bMetric = allLocationMetrics?.get(b.id)?.[selectedSortMetric] ?? 0;
        return aMetric - bMetric;
      })
    : [...selectedLocations];

  const finalSortedLocations = reverseOrder ? sortedLocations.reverse() : sortedLocations;
  const selectedMetrics = Object.entries(showMetrics)
    .filter(([, isVisible]) => isVisible)
    .map(([key]) => key);

  useEffect(() => {
    async function setUpKinesis(): Promise<void> {
      const userCredentials = await Auth.currentCredentials();
      AWS.config.update({
        region: import.meta.env.VITE_AWS_REGION,
        credentials: Auth.essentialCredentials(userCredentials),
      });
      setKinesisVideo(new AWS.KinesisVideo());
    }
    setUpKinesis().catch((error) => {
      console.error('Error setting up Kinesis', error);
    });
  }, []);

  useEffect(() => {
    if (selectedFranchise && selectedLocations.length > 0) {
      const locationIDs = selectedLocations.map((obj) => obj.id);
      const fetchMetrics = async () => {
        try {
          setLoading(true);
          const locationMetrics = await getMothershipMetrics(selectedFranchise.id, locationIDs);
          setAllLocationMetrics(new Map(Object.entries(locationMetrics)));
        } catch (error) {
          console.error('Error fetching location metrics:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchMetrics();
    }
  }, [selectedFranchise, selectedLocations]);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedLocations, selectedSortMetric, reverseOrder, perPage]);

  useEffect(() => {
    const itemsPerPage = typeof perPage === 'number' ? perPage : finalSortedLocations.length;
    const maxPage = Math.ceil(finalSortedLocations.length / itemsPerPage) - 1;
    if (currentPage > maxPage) {
      setCurrentPage(Math.max(0, maxPage));
    }
  }, [finalSortedLocations.length, currentPage, perPage]);

  useEffect(() => {
    setHasCvStream(true);
    setHasCvDtStream(true);
    setHasMonitorStream(true);
  }, []);

  useEffect(() => {
    setShowCvStream(false);
    setShowCvDtStream(false);
    setShowMonitorStream(false);
  }, [selectedFranchise]);

  useEffect(() => {
    if (showCvStream) {
      setShowCvDtStream(false);
      setShowMonitorStream(false);
    }
  }, [showCvStream]);

  useEffect(() => {
    if (showCvDtStream) {
      setShowCvStream(false);
      setShowMonitorStream(false);
    }
  }, [showCvDtStream]);

  useEffect(() => {
    if (showMonitorStream) {
      setShowCvStream(false);
      setShowCvDtStream(false);
    }
  }, [showMonitorStream]);

  const itemsPerPage = typeof perPage === 'number' ? perPage : finalSortedLocations.length;
  const totalPages = Math.ceil(finalSortedLocations.length / itemsPerPage);
  const displayedLocations = finalSortedLocations.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  const camera = showCvStream ? 'CV' : showCvDtStream ? 'CV_DT' : 'MONITOR';

  return (
    <div className="cont">
      <div className="row">
        <div className="col-10 col-m-10 p-2 mx-auto"></div>
        <div className="flex flex-wrap justify-start gap-2 col-10 col-m-10 p-2 mx-auto">
          <DropdownFranchise />
          <DropdownLocationMultiple />
          <LiveButton />
          <Button variant="contained" color="primary" onClick={() => setReverseOrder((prev) => !prev)}>
            Reverse Order
          </Button>
          <ToggleButtonGroup
            value={perPage}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setPerPage(newValue);
                setCurrentPage(0);
              }
            }}
            sx={{
              '& .MuiToggleButton-root': {
                backgroundColor: 'grey.200',
                color: 'black',
                '&:hover': { backgroundColor: 'grey.300' },
                '&.Mui-selected': {
                  backgroundColor: 'grey.500',
                  color: 'white',
                  '&:hover': { backgroundColor: 'grey.400' },
                },
              },
            }}
          >
            <ToggleButton value="all">Full View</ToggleButton>
            <ToggleButton value={8}>8 per page</ToggleButton>
            <ToggleButton value={12}>12 per page</ToggleButton>
          </ToggleButtonGroup>
          <div className="ml-auto flex gap-2">
            {(showCvToggle || shouldHaveMonitorStreams) && hasMonitorStream && <CvToggleButton type="MONITOR" />}
            {(showCvToggle || shouldHaveCVDTStream) && hasCvDtStream && <CvToggleButton type="DT" />}
            {showCvToggle && hasCvStream && <CvToggleButton type="LOBBY" />}
          </div>
        </div>
        <ToggleButtonGroup
          value={selectedMetrics}
          onChange={(event, newValues) => {
            setShowMetrics((prev) => ({
              ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
              ...newValues.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
            }));
          }}
          sx={{
            '& .MuiToggleButton-root': {
              backgroundColor: 'grey.200',
              color: 'black',
              '&:hover': { backgroundColor: 'grey.300' },
              '&.Mui-selected': {
                backgroundColor: 'grey.500',
                color: 'white',
                '&:hover': { backgroundColor: 'grey.400' },
              },
            },
          }}
        >
          {Object.keys(showMetrics).map((metric) => (
            <ToggleButton key={metric} value={metric}>
              {metric.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          value={selectedSortMetric}
          onChange={(event, newValue) => {
            setSelectedSortMetric(newValue);
            setReverseOrder(false);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              backgroundColor: 'grey.200',
              color: 'black',
              '&:hover': { backgroundColor: 'grey.300' },
              '&.Mui-selected': {
                backgroundColor: 'grey.500',
                color: 'white',
                '&:hover': { backgroundColor: 'grey.400' },
              },
            },
          }}
        >
          {Object.keys(showMetrics).map((metric) => (
            <ToggleButton key={metric} value={metric}>
              Sort by {metric.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>
      <Button variant="contained" onClick={() => setShowMetricsView((prev) => !prev)}>
        {showMetricsView ? 'Switch to Video View' : 'Switch to Metrics View'}
      </Button>
      {perPage !== 'all' && (
        <div className="flex justify-center items-center my-4">
          <Button
            variant="outlined"
            disabled={currentPage === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="mx-4">Page {currentPage + 1} of {totalPages}</span>
          <Button
            variant="outlined"
            disabled={currentPage === totalPages - 1 || totalPages === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-4">
        {loading ? (
          <div>Loading metrics...</div>
        ) : (
          displayedLocations.map((location) => (
            <div key={location.id}>
              {showMetricsView ? (
                <MetricsDisplayer
                  location={location}
                  camera={camera}
                  metrics={allLocationMetrics?.get(location.id) || {}}
                  franchise={selectedFranchise}
                  showMetrics={showMetrics}
                />
              ) : (
                <VideoPlayerKinesis
                  location={location}
                  camera={camera}
                  metrics={allLocationMetrics?.get(location.id) || {}}
                  franchise={selectedFranchise}
                  showMetrics={showMetrics}
                />
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Mothership;